import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'

import { useAnalytics } from '@sketch/modules-common'

import { useCanvasHost } from '../host'
import { useCanvasScope } from '../scope'
import type { GoToArtboardEventActionType } from '@sketch/canvas'

/**
 * Opens a top-level entity of a known kind from the canvas.
 *
 * The implementation behind useNavigateToFrame and useNavigateToSection.
 * useNavigateToEntity sits above all three, for callers that have only a UUID
 * and need the kind resolved first.
 *
 * One implementation for both kinds of entity and both hosts: the route to push
 * comes from the host that owns the document - a Share keeps the version being
 * viewed, a Preview has its own routes - and the page it needs comes from the
 * canvas scope, which has already resolved the fallbacks the URL leaves out
 * (the Share document route and the root Preview route both omit the page).
 */
export function useNavigateToKind(kind: 'frame' | 'section') {
  const history = useHistory()
  const host = useCanvasHost()
  const scope = useCanvasScope()
  const { trackEvent } = useAnalytics()

  return useCallback(
    (entityUUID: string, actionType: GoToArtboardEventActionType) => {
      if (!host || !scope) {
        return
      }

      const pathname =
        kind === 'frame'
          ? host.links.frame(scope.pageUUID, entityUUID)
          : host.links.section(scope.pageUUID, entityUUID)

      if (!pathname) {
        return
      }

      trackEvent('CANVAS - open artboard', { actionType })

      // A Preview carries the sidebar-tab hash across navigation; the Share
      // views never have. Kept as it was rather than unified here.
      history.push(
        host.kind === 'preview'
          ? { pathname, hash: history.location.hash }
          : pathname
      )
    },
    [kind, host, scope, history, trackEvent]
  )
}
